import { gql } from '@apollo/client';
// Fragments
import {
  DueDateFields,
  CounterOfferFields,
  CampaignSearchLocationFields,
} from './fragments/index';

export const GET_CAMPAIGNS_V2 = gql`
  query GetCampaignsV2($input: GetCampaignsInput!) {
    getCampaigns(input: $input) {
      entities {
        v
        isPrivate
        requiresAgreement
        agreementSignatoryId
        agreementSignatory {
          id
          firstName
          lastName
          email
        }
        usageRights
        usageRightsText
        usageRightsDuration {
          years
          months
        }
        usageRightsScopes
        estimatedBudget
        questions {
          id
          question
          order
          type
          isRequired
        }
        agency {
          id
          email
          phoneNumber
          firstName
          lastName
          agencyDetails {
            companyName
          }
        }
        collaborators {
          role {
            roleId
          }
          user {
            id
            firstName
            lastName
            email
          }
        }
        activities {
          ... on SocialActivity {
            id
            avoid
            brandName
            encouraged
            files {
              id
              key
              url
            }
            hashtags
            instructions
            mediaType
            physicalProduct
            physicalProductType
            productName
            productValue
            profileMentions
            promoCodes
            promoUrls
            quantityInstructions
            script
            submissionDue
            suggestedCaption
            tasks {
              id
              type
              orderIdx
              title
              description
              ... on CampaignContentTask {
                mediaType
                dueDate {
                  ...DueDateFields
                }
              }
            }
            tasksV2 {
              id
              type
              hrType
              hrPlatform
              orderIdx
              title
              description
              suggestedCaption
              ... on CampaignContentTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGLiveTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGPostTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGReelTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGStoryTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTVideoTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTLiveTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTStoryTask {
                dueDate {
                  ...DueDateFields
                }
              }
              contentItems {
                id
                type
                title
                description
                orderIdx
                ... on CampaignContentTask {
                  mediaType
                  dueDate {
                    ...DueDateFields
                  }
                }
              }
            }
            website
            postDueFrom
            postDueTo
          }
          ... on SponsoredAppearanceActivity {
            id
            type
            videoType
            withAmplify
            target {
              ... on SponsoredAppearanceGuest {
                id
                type
                instagram
                instructions
                linkedin
                otherSocial
                guestName: name
                guestBio: bio
                company
                email
              }
              ... on SponsoredAppearanceProduct {
                id
                type
                instagram
                instructions
                linkedin
                otherSocial
                brandName: name
                productBio: bio
                website
                physicalProduct
                physicalProductType
                productName
                productValue
                files {
                  id
                  key
                  url
                }
              }
            }
          }
          ... on InPersonAppearanceActivity {
            id
            address
            state
            type
            venueName
            zip
            city
            country
            coveredExpenses
            description
            duration
            files {
              id
              key
              url
            }
            instructions
            possibleDates
          }
          ... on VirtualAppearanceActivity {
            id
            description
            duration
            files {
              id
              key
              url
            }
            instructions
            location
            possibleDates
          }
          ... on CustomActivity {
            id
            instructions
            files {
              id
              key
              url
            }
          }
          ... on EventMarketingActivity {
            id
            description
            estimatedViews
            participantCount
            shortFormVideoCount
            socialMediaLinks
            sport
            startDate
            type
          }
          ... on SocialShareActivity {
            id
            brandName
            socialShareDescription: description
            website
            instructions
            shareUrl
            type
            tasks {
              id
              type
            }
          }
          ... on SocialBoostActivity {
            id
            brandName
            postUrl
            caption
            tags
            website
            linkStickerUrl
            linkStickerTitle
            type
          }
          ... on UgcActivity {
            id
            avoid
            brandName
            encouraged
            files {
              id
              key
              url
            }
            instructions
            physicalProduct
            physicalProductType
            productName
            productValue
            promoCodes
            promoUrls
            tasks {
              id
              type
              orderIdx
              title
              description
              ... on CampaignContentTask {
                mediaType
                dueDate {
                  ...DueDateFields
                }
              }
            }
            website
          }
          ... on CustomBriefActivity {
            id
            type
            instructions
            brandName
            website
            encouraged
            avoid
            physicalProduct
            productValue
            products {
              name
              description
              cost
              qty
              shipping
            }
            promoCodes
            promoUrls
            contracts {
              id
              key
              url
              kind
            }
            tasksV2 {
              id
              type
              hrType
              hrPlatform
              orderIdx
              title
              description
              suggestedCaption
              hashtags
              mentions
              tagAsCollab
              examples {
                id
                key
                url
              }
              ... on CampaignContentTask {
                mediaType
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGLiveTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGPostTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGReelTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignIGStoryTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTVideoTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTLiveTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignTTStoryTask {
                dueDate {
                  ...DueDateFields
                }
              }
              ... on CampaignAppearanceTask {
                appearanceType
                durationText
                location
                travelIncluded
                accommodationIncluded
                maxExpenses
                dueDate {
                  ...DueDateFields
                }
              }
              contentItems {
                id
                type
                title
                description
                orderIdx
                ... on CampaignContentTask {
                  mediaType
                  dueDate {
                    ...DueDateFields
                  }
                }
              }
            }
          }
        }
        brandName
        budget
        budgetLeft
        reservedBudget
        funding {
          netPaid
          netPromo
          paidIn
          paidOut
          promoIn
          promoOut
        }
        launchFee
        platformMarginPercent
        inviteProcessingFee
        createdAt
        dueDate
        flatFeePerStore
        id
        name
        internalName
        startMode
        startAt
        search {
          id
          athleteMaxAge
          athleteMinAge
          budget
          flatFeePerStore
          genders
          hometownLocations {
            ...CampaignSearchLocationFields
          }
          engagementRateFrom
          engagementRateTo
          hashtags
          locations {
            ...CampaignSearchLocationFields
          }
          excludedLocations {
            ...CampaignSearchLocationFields
          }
          maxFollowers
          minFollowers
          roles
          smPlatforms
          sports
          audienceAgeRanges
          audienceEthnicities
          audienceGender
          audienceInterests
          audienceLocations {
            ...CampaignSearchLocationFields
          }
          ... on SponsoredAppearanceSearchCriteria {
            appearanceTargetType
            appearanceVideoType
            withAmplify
            athleteMinAge
            athleteMaxAge
            budget
            flatFeePerStore
            genders
            id
            locations {
              ...CampaignSearchLocationFields
            }
            maxFollowers
            minFollowers
            roles
            smPlatforms
            sports
            type
          }
          ... on BookMeSearchCriteria {
            id
            athleticLevels
            budgetRangeFrom
            budgetRangeTo
            athleteMaxAge
            athleteMinAge
            budget
            flatFeePerStore
            genders
            maxFollowers
            minFollowers
            smPlatforms
            sports
            roles
            talentId
            talent {
              id
              firstName
              lastName
              storeDetails {
                storeName
              }
            }
            hashtags
            hometownLocations {
              ...CampaignSearchLocationFields
            }
            locations {
              ...CampaignSearchLocationFields
            }
          }
          ... on SocialBoostSearchCriteria {
            platformCriteria {
              id
              platform
              minFollowers
              maxFollowers
              minEngagementRate
              maxEngagementRate
            }
          }
          ... on CustomSearchCriteria {
            id
            type
            athleteMaxAge
            athleteMinAge
            budget
            flatFeePerStore
            excludeWorkedWith
            excludeDeclined
            influencerCategoryIds
            genders
            roles
            sports
            smPlatforms
            maxFollowers
            minFollowers
            engagementRateFrom
            engagementRateTo
            hashtags
            platformCriteria {
              id
              platform
              minFollowers
              maxFollowers
              minEngagementRate
              maxEngagementRate
            }
            locations {
              ...CampaignSearchLocationFields
            }
            hometownLocations {
              ...CampaignSearchLocationFields
            }
            audienceAgeRanges
            audienceEthnicities
            audienceGender
            audienceInterests
            audienceLocations {
              ...CampaignSearchLocationFields
            }
            athleteAgeRanges {
              minAge
              maxAge
            }
          }
        }
        status
        totalAthletes
        totalCost
        totalFollowers
        totalOriginalAthletes
        type
        updatedAt
        v
        applicationDeadlineDays
        applicationDeadlineEnabled
      }
      limit
      offset
      total
    }
  }
  ${DueDateFields}
  ${CampaignSearchLocationFields}
`;

export const GET_CAMPAIGN = gql`
  query GetCampaign($input: GetCampaignInput!) {
    getCampaign(input: $input) {
      v
      isPrivate
      usageRights
      usageRightsText
      usageRightsDuration {
        years
        months
      }
      usageRightsScopes
      estimatedBudget
      questions {
        id
        question
        order
        type
        isRequired
      }
      agency {
        id
        email
        phoneNumber
        firstName
        lastName
        agencyDetails {
          companyName
        }
      }
      collaborators {
        role {
          roleId
        }
        user {
          id
          firstName
          lastName
          email
        }
      }
      activities {
        ... on SocialActivity {
          id
          avoid
          brandName
          encouraged
          files {
            id
            key
            url
          }
          hashtags
          instructions
          mediaType
          physicalProduct
          physicalProductType
          productName
          productValue
          profileMentions
          promoCodes
          promoUrls
          quantityInstructions
          script
          submissionDue
          suggestedCaption
          tasks {
            id
            type
            orderIdx
            title
            description
            ... on CampaignContentTask {
              mediaType
              dueDate {
                ...DueDateFields
              }
            }
          }
          tasksV2 {
            id
            type
            hrType
            hrPlatform
            orderIdx
            title
            description
            suggestedCaption
            ... on CampaignContentTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGLiveTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGPostTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGReelTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGStoryTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTVideoTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTLiveTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTStoryTask {
              dueDate {
                ...DueDateFields
              }
            }
            contentItems {
              id
              type
              title
              description
              orderIdx
              ... on CampaignContentTask {
                mediaType
                dueDate {
                  ...DueDateFields
                }
              }
            }
          }
          website
          postDueFrom
          postDueTo
        }
        ... on SponsoredAppearanceActivity {
          id
          type
          videoType
          withAmplify
          target {
            ... on SponsoredAppearanceGuest {
              id
              type
              instagram
              instructions
              linkedin
              otherSocial
              guestName: name
              guestBio: bio
              company
              email
            }
            ... on SponsoredAppearanceProduct {
              id
              type
              instagram
              instructions
              linkedin
              otherSocial
              brandName: name
              productBio: bio
              website
              physicalProduct
              physicalProductType
              productName
              productValue
              files {
                id
                key
                url
              }
            }
          }
        }
        ... on InPersonAppearanceActivity {
          id
          address
          state
          type
          venueName
          zip
          city
          country
          coveredExpenses
          description
          duration
          files {
            id
            key
            url
          }
          instructions
          possibleDates
        }
        ... on VirtualAppearanceActivity {
          id
          description
          duration
          files {
            id
            key
            url
          }
          instructions
          location
          possibleDates
        }
        ... on CustomActivity {
          id
          instructions
          files {
            id
            key
            url
          }
        }
        ... on EventMarketingActivity {
          id
          description
          estimatedViews
          participantCount
          shortFormVideoCount
          socialMediaLinks
          sport
          startDate
          type
        }
        ... on SocialShareActivity {
          id
          brandName
          socialShareDescription: description
          website
          instructions
          shareUrl
          type
          tasks {
            id
            type
          }
        }
        ... on UgcActivity {
          id
          avoid
          brandName
          encouraged
          files {
            id
            key
            url
          }
          instructions
          physicalProduct
          physicalProductType
          productName
          productValue
          promoCodes
          promoUrls
          tasks {
            id
            type
            orderIdx
            title
            description
            ... on CampaignContentTask {
              mediaType
              dueDate {
                ...DueDateFields
              }
            }
          }
          website
        }
        ... on CustomBriefActivity {
          id
          type
          instructions
          brandName
          website
          encouraged
          avoid
          physicalProduct
          productValue
          products {
            name
            description
            cost
            qty
            shipping
          }
          promoCodes
          promoUrls
          contracts {
            id
            key
            url
            kind
          }
          tasksV2 {
            id
            type
            hrType
            hrPlatform
            orderIdx
            title
            description
            suggestedCaption
            hashtags
            mentions
            tagAsCollab
            examples {
              id
              key
              url
            }
            ... on CampaignContentTask {
              mediaType
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGLiveTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGPostTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGReelTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGStoryTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTVideoTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTLiveTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTStoryTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignAppearanceTask {
              appearanceType
              durationText
              location
              travelIncluded
              accommodationIncluded
              maxExpenses
              dueDate {
                ...DueDateFields
              }
            }
            contentItems {
              id
              type
              title
              description
              orderIdx
              ... on CampaignContentTask {
                mediaType
                dueDate {
                  ...DueDateFields
                }
              }
            }
          }
        }
      }
      brandName
      budget
      budgetLeft
      reservedBudget
      funding {
        netPaid
        netPromo
        paidIn
        paidOut
        promoIn
        promoOut
      }
      launchFee
      platformMarginPercent
      inviteProcessingFee
      createdAt
      dueDate
      flatFeePerStore
      id
      name
      internalName
      search {
        id
        athleteMaxAge
        athleteMinAge
        budget
        flatFeePerStore
        genders
        hometownLocations {
          ...CampaignSearchLocationFields
        }
        engagementRateFrom
        engagementRateTo
        hashtags
        locations {
          ...CampaignSearchLocationFields
        }
        excludedLocations {
          ...CampaignSearchLocationFields
        }
        maxFollowers
        minFollowers
        roles
        smPlatforms
        sports
        audienceAgeRanges
        audienceEthnicities
        audienceGender
        audienceInterests
        audienceLocations {
          ...CampaignSearchLocationFields
        }
        ... on SponsoredAppearanceSearchCriteria {
          appearanceTargetType
          appearanceVideoType
          withAmplify
          athleteMinAge
          athleteMaxAge
          budget
          flatFeePerStore
          genders
          id
          locations {
            ...CampaignSearchLocationFields
          }
          maxFollowers
          minFollowers
          roles
          smPlatforms
          sports
          type
        }
        ... on BookMeSearchCriteria {
          id
          athleticLevels
          budgetRangeFrom
          budgetRangeTo
          athleteMaxAge
          athleteMinAge
          budget
          flatFeePerStore
          genders
          maxFollowers
          minFollowers
          smPlatforms
          sports
          roles
          talentId
          talent {
            id
            firstName
            lastName
            storeDetails {
              storeName
            }
          }
          hashtags
          hometownLocations {
            ...CampaignSearchLocationFields
          }
          locations {
            ...CampaignSearchLocationFields
          }
        }
        ... on SocialBoostSearchCriteria {
          platformCriteria {
            id
            platform
            minFollowers
            maxFollowers
            minEngagementRate
            maxEngagementRate
          }
        }
        ... on CustomSearchCriteria {
          id
          type
          athleteMaxAge
          athleteMinAge
          budget
          flatFeePerStore
          excludeWorkedWith
          excludeDeclined
          influencerCategoryIds
          genders
          roles
          sports
          smPlatforms
          maxFollowers
          minFollowers
          engagementRateFrom
          engagementRateTo
          hashtags
          platformCriteria {
            id
            platform
            minFollowers
            maxFollowers
            minEngagementRate
            maxEngagementRate
          }
          locations {
            ...CampaignSearchLocationFields
          }
          hometownLocations {
            ...CampaignSearchLocationFields
          }
          audienceAgeRanges
          audienceEthnicities
          audienceGender
          audienceInterests
          audienceLocations {
            ...CampaignSearchLocationFields
          }
          athleteAgeRanges {
            minAge
            maxAge
          }
        }
      }
      status
      totalAthletes
      totalCost
      totalFollowers
      totalOriginalAthletes
      type
      updatedAt
      v
      applicationDeadlineDays
      applicationDeadlineEnabled
    }
  }
  ${DueDateFields}
  ${CampaignSearchLocationFields}
`;

export const GET_PUBLIC_CAMPAIGN_COLLAB_ROLES = gql`
  query GetPublicCampaignCollabRoles {
    getPublicCampaignCollabRoles {
      roleId
      permissions {
        name
      }
    }
  }
`;

export const GET_CAMPAIGN_OFFERS = gql`
  query GetCampaignOffers($input: GetCampaignOffersInput!) {
    getCampaignOffers(input: $input) {
      limit
      offset
      total
      entities {
        id
        appliedAt
        approvedAt
        userId
        firstName
        lastName
        profileName
        socialFollowing
        counterOfferAttention
        originalTotalFee
        originalInfluencerFee
        estimatedTotalFee
        estimatedInfluencerFee
        estimatedPlatformFee
        hasSignedAgreement
        counterOffers {
          ...CounterOfferFields
        }
        deliverablesV2 {
          id
          createdAt
          updatedAt
          status
          contentDueDateUtc
          contentDueDateFrom
          contentDueDateTo
          publishDueDateUtc
          publishDueDateFrom
          publishDueDateTo
          extendedPublishDays
          task {
            id
            type
            title
            description
            orderIdx
            suggestedCaption
            ... on CampaignContentTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGLiveTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGPostTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGReelTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGStoryTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTVideoTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTLiveTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTStoryTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignAppearanceTask {
              appearanceType
              location
              durationText
              dueDate {
                ...DueDateFields
              }
            }
          }
          items {
            id
            type
            status
            taskId
            rejectCnt
            task {
              id
              type
              title
              description
              suggestedCaption
              orderIdx
              ... on CampaignContentTask {
                mediaType
              }
            }
            ... on CbDeliverableFilesItemV2 {
              files {
                key
                name
                type
                url
                originalUrl
              }
            }
            ... on CbDeliverableFileItemV2 {
              key
              name
              url
              originalUrl
              videoTranscodingJob {
                id
                resultKey
                status
              }
            }
            ... on CbDeliverableCopyV2 {
              copy
            }
          }
          revisions {
            id
            createdAt
            isArchived
            performedById
            performedByEmail
            items {
              id
              type
              taskDescription
              taskTitle
              feedback
              itemId
            }
          }
        }
        user {
          socialMedia {
            engagementRate
            error
            followingCnt
            id
            profileName
            sourceKey
            type
            url
            userId
            username
          }
        }
        submittedAt
        statusV2
        submissions {
          id
          createdAt
          task {
            id
          }
          type
          ... on CampaignLiveAppearanceSubmission {
            streamCohostToken
            clipUrls
            clipThumbUrls
            mediaLinks
            postImageUrl
            event {
              schedule {
                timezone
              }
            }
            recordUrls
            stream {
              id
              name
              description
              slug
              imageFileKey
              imageURL
              mainImageUrl
              scheduleDateUtc
              scheduleDateTz
              streamStatus
              hashtags {
                id
                name
              }
              mentions {
                id
                mentionType
                store {
                  id
                }
                targetType
                unregisteredContact {
                  id
                }
              }
              store {
                slug
              }
            }
          }
          ... on CampaignRecordedAppearanceSubmission {
            streamCohostToken
            clipUrls
            clipThumbUrls
            mediaLinks
            postImageUrl
            event {
              schedule {
                timezone
              }
            }
            recordUrls
            stream {
              id
              name
              description
              slug
              imageFileKey
              imageURL
              mainImageUrl
              scheduleDateUtc
              scheduleDateTz
              streamStatus
              hashtags {
                id
                name
              }
              mentions {
                id
                mentionType
                store {
                  id
                }
                targetType
                unregisteredContact {
                  id
                }
              }
              store {
                slug
              }
            }
            draftStream {
              id
              description
              slug
              imageFileKey
              imageURL
              mainImageUrl
              name
              scheduleDateUtc
              streamStatus
              scheduleDateTz
              hashtags {
                id
                name
              }
              mentions {
                id
                mentionType
                store {
                  id
                }
                targetType
                unregisteredContact {
                  id
                }
              }
              store {
                slug
              }
            }
          }

          ... on CampaignIGLiveSubmission {
            imageKey
            url
          }
          ... on CampaignIGPostSubmission {
            link
          }
          ... on CampaignIGReelSubmission {
            link
          }
          ... on CampaignIGStorySubmission {
            imageKey
            url
          }
          ... on CampaignTTVideoSubmission {
            link
          }
          ... on CampaignTWTweetSubmission {
            link
          }
          ... on CampaignTWVideoSubmission {
            link
          }
          ... on CampaignTWLiveSubmission {
            link
          }
          ... on CampaignIGShareSubmission {
            shareLink: link
            shareImage: imageKey
            shareUrl: url
            type
          }
          ... on CampaignTTShareSubmission {
            shareLink: link
            shareImage: imageKey
            shareUrl: url
            type
          }
          ... on CampaignTWShareSubmission {
            shareLink: link
            shareImage: imageKey
            shareUrl: url
            type
          }
          ... on CampaignFBShareSubmission {
            shareLink: link
            shareImage: imageKey
            shareUrl: url
            type
          }
          ... on CampaignTWShareSubmission {
            shareLink: link
            shareImage: imageKey
            shareUrl: url
            type
          }
          ... on CampaignYTShareSubmission {
            shareLink: link
            shareImage: imageKey
            shareUrl: url
            type
          }
        }
        campaign {
          id
          budgetReserved
          search {
            id
            smPlatforms
          }
          agency {
            id
          }
          activities {
            ... on SocialActivity {
              id
              type
              tasks {
                id
                type
                orderIdx
                title
                description
                ... on CampaignContentTask {
                  mediaType
                }
              }
              files {
                id
                key
                url
              }
              tasksV2 {
                id
                type
                hrType
                hrPlatform
                orderIdx
                title
                description
                suggestedCaption
                ... on CampaignContentTask {
                  dueDate {
                    ...DueDateFields
                  }
                }
                ... on CampaignIGLiveTask {
                  dueDate {
                    ...DueDateFields
                  }
                }
                ... on CampaignIGPostTask {
                  dueDate {
                    ...DueDateFields
                  }
                }
                ... on CampaignIGReelTask {
                  dueDate {
                    ...DueDateFields
                  }
                }
                ... on CampaignIGStoryTask {
                  dueDate {
                    ...DueDateFields
                  }
                }
                ... on CampaignTTVideoTask {
                  dueDate {
                    ...DueDateFields
                  }
                }
                ... on CampaignTTLiveTask {
                  dueDate {
                    ...DueDateFields
                  }
                }
                ... on CampaignTTStoryTask {
                  dueDate {
                    ...DueDateFields
                  }
                }
                contentItems {
                  id
                  type
                  title
                  description
                  orderIdx
                  ... on CampaignContentTask {
                    mediaType
                    dueDate {
                      ...DueDateFields
                    }
                  }
                }
              }
            }
            ... on SocialShareActivity {
              id
              brandName
              website
              description
              instructions
              shareUrl
              tasks {
                id
                type
              }
              type
            }
          }
          type
          v
        }
        deliverables {
          agencyFeedback
          createdAt
          files {
            id
            key
            name
            url
          }
          id
          postCopy
          status
          items {
            id
            type
            ... on CBDeliverableCopy {
              id
              copy
              type
            }
            ... on CBDeliverableFileItem {
              id
              key
              name
              type
              url
              originalUrl
            }
            task {
              id
              type
              title
              description
            }
          }
        }
        deliverablesV2 {
          id
          createdAt
          updatedAt
          status
          contentDueDateUtc
          contentDueDateFrom
          contentDueDateTo
          publishDueDateUtc
          publishDueDateFrom
          publishDueDateTo
          task {
            id
            type
            title
            description
            orderIdx
            ... on CampaignContentTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGLiveTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGPostTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGReelTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignIGStoryTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTVideoTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTLiveTask {
              dueDate {
                ...DueDateFields
              }
            }
            ... on CampaignTTStoryTask {
              dueDate {
                ...DueDateFields
              }
            }
          }
          items {
            id
            type
            status
            taskId
            rejectCnt
            task {
              id
              type
              title
              description
              orderIdx
              ... on CampaignContentTask {
                mediaType
              }
            }
            ... on CbDeliverableFilesItemV2 {
              files {
                key
                name
                type
                url
                originalUrl
              }
            }
            ... on CbDeliverableFileItemV2 {
              key
              name
              url
              originalUrl
              videoTranscodingJob {
                id
                resultKey
                status
              }
            }
            ... on CbDeliverableCopyV2 {
              copy
            }
          }
          revisions {
            id
            createdAt
            isArchived
            performedById
            performedByEmail
            items {
              id
              type
              taskDescription
              taskTitle
              feedback
              itemId
            }
          }
        }
      }
    }
  }
  ${DueDateFields}
  ${CounterOfferFields}
`;

export const SEARCH_CAMPAIGN_STORES = gql`
  query SearchCampaignStores($input: SearchCampaignStoresInput!) {
    searchCampaignStores(input: $input) {
      entities {
        id
        firstName
        lastName
        email
        slug
        socialMedia {
          engagementRate
          error
          followingCnt
          id
          profileName
          sourceKey
          type
          url
          userId
          username
        }
        sports {
          id
          name
        }
        storeDetails {
          storeId
          storeName
          avatarURL
          country
          gender
          socialFollowing
          birthDate
        }
      }
      summary {
        totalCost
        cost
        followers
      }
      searchCriteria {
        id
        athleteMaxAge
        athleteMinAge
        budget
        flatFeePerStore
        genders
        maxFollowers
        minFollowers
        smPlatforms
        sports
        locations {
          country
          state
          city
          latitude
          longitude
          placeId
          placeText
          radius
        }
      }
      limit
      offset
      total
    }
  }
`;

export const SEARCH_UGC_STORES = gql`
  query SearchUgcStores($input: SearchUgcStoresInput!) {
    searchUgcStores(input: $input) {
      entities {
        id
        firstName
        lastName
        email
        slug
        socialMedia {
          engagementRate
          error
          followingCnt
          id
          profileName
          sourceKey
          type
          url
          userId
          username
        }
        sports {
          id
          name
        }
        storeDetails {
          storeId
          storeName
          avatarURL
          country
          gender
          socialFollowing
          birthDate
        }
      }
      summary {
        totalCost
        cost
        followers
      }
      searchCriteria {
        id
        athleteMaxAge
        athleteMinAge
        budget
        flatFeePerStore
        genders
        maxFollowers
        minFollowers
        sports
        locations {
          country
          state
          city
          latitude
          longitude
          placeId
          placeText
          radius
        }
      }
      limit
      offset
      total
    }
  }
`;

export const SEARCH_CUSTOM_STORES = gql`
  query SearchCustomStores($input: SearchCustomStoresInput!) {
    searchCustomStores(input: $input) {
      entities {
        id
        firstName
        lastName
        email
        slug
        socialMedia {
          engagementRate
          error
          followingCnt
          id
          profileName
          sourceKey
          type
          url
          userId
          username
        }
        sports {
          id
          name
        }
        storeDetails {
          storeId
          storeName
          avatarURL
          country
          gender
          socialFollowing
          birthDate
        }
      }
      summary {
        totalCost
        cost
        followers
      }
      limit
      offset
      total
    }
  }
`;

export const SEARCH_SPONSORED_APPEARANCE_CAMPAIGN_STORES = gql`
  query SearchSponsoredAppearanceCampaignStores(
    $input: SearchSponsoredAppearanceInput!
  ) {
    searchSponsoredAppearanceCampaignStores(input: $input) {
      entities {
        id
        firstName
        lastName
        email
        slug
        sports {
          id
          name
        }
        socialMedia {
          engagementRate
          error
          followingCnt
          id
          profileName
          sourceKey
          type
          url
          userId
          username
        }
        storeDetails {
          storeId
          storeName
          avatarURL
          socialFollowing
          country
          gender
          socialFollowing
          birthDate
        }
      }
      searchCriteria {
        appearanceTargetType
        appearanceVideoType
        withAmplify
        athleteMinAge
        athleteMaxAge
        budget
        flatFeePerStore
        genders
        id
        locations {
          country
          state
          city
          latitude
          longitude
          placeId
          placeText
          radius
        }
        maxFollowers
        minFollowers
        roles
        smPlatforms
        sports
        type
      }
      summary {
        cost
        followers
        totalCost
      }
      limit
      offset
      total
    }
  }
`;

export const GET_AGENCIES = gql`
  query GetAgencies($input: GetAgenciesInput!) {
    getAgencies(input: $input) {
      entities {
        id
        email
        firstName
        lastName
        phoneNumber
        talentCrmCount
        agencyDetails {
          companyDomains
          companyName
          city
          country
          id
          jobTitle
          latitude
          longitude
          state
        }
      }
      limit
      offset
      total
    }
  }
`;

export const GET_CREDIT_USAGE_REPORT = gql`
  query GetCreditUsageReport($input: GetAgenciesInput!) {
    getAgencies(input: $input) {
      entities {
        id
        email
        firstName
        lastName
        creditsRemaining
        creditsUsed
        lastDateCreditUsed
        agencyDetails {
          companyName
        }
      }
      limit
      offset
      total
    }
  }
`;

export const GET_AGENCY_CREDIT_USAGE = gql`
  query GetAgencyCreditUsage($input: GetAgencyCreditUsageInput!) {
    getAgencyCreditUsage(input: $input) {
      entities {
        id
        platform
        handle
        createdAt
      }
      limit
      offset
      total
    }
  }
`;

export const SEARCH_SOCIAL_SHARE_CAMPAIGN_STORES = gql`
  query SearchSocialShareCampaignStores(
    $input: SearchSocialShareCampaignInput!
  ) {
    searchSocialShareCampaignStores(input: $input) {
      entities {
        id
        firstName
        lastName
        email
        slug
        sports {
          id
          name
        }
        socialMedia {
          engagementRate
          error
          followingCnt
          id
          profileName
          sourceKey
          type
          url
          userId
          username
        }
        storeDetails {
          storeId
          storeName
          avatarURL
          socialFollowing
          country
          gender
          socialFollowing
          birthDate
        }
      }
      summary {
        totalCost
        cost
        followers
      }
      searchCriteria {
        id
        athleteMaxAge
        athleteMinAge
        budget
        flatFeePerStore
        genders
        maxFollowers
        minFollowers
        smPlatforms
        sports
        roles
        locations {
          country
          state
          city
          latitude
          longitude
          placeId
          placeText
          radius
        }
      }
      limit
      offset
      total
    }
  }
`;

export const SEARCH_SOCIAL_BOOST_CAMPAIGN_STORES = gql`
  query SearchSocialBoostCampaignStores(
    $input: SearchSocialBoostCampaignInput!
  ) {
    searchSocialBoostCampaignStores(input: $input) {
      entities {
        id
        firstName
        lastName
        email
        slug
        sports {
          id
          name
        }
        socialMedia {
          engagementRate
          error
          followingCnt
          id
          profileName
          sourceKey
          type
          url
          userId
          username
        }
        storeDetails {
          storeId
          storeName
          avatarURL
          socialFollowing
          country
          gender
          birthDate
        }
      }
      summary {
        totalCost
        cost
        followers
      }
      searchCriteria {
        id
        athleteMaxAge
        athleteMinAge
        budget
        flatFeePerStore
        genders
        maxFollowers
        minFollowers
        smPlatforms
        sports
        roles
        locations {
          country
          state
          city
          latitude
          longitude
          placeId
          placeText
          radius
        }
      }
      limit
      offset
      total
    }
  }
`;

export const SEARCH_BOOK_ME_CAMPAIGN_STORES = gql`
  query SearchBookMeCampaignStores($input: SearchBookMeCampaignInput!) {
    searchBookMeStores(input: $input) {
      entities {
        id
        firstName
        lastName
        email
        slug
        sports {
          id
          name
        }
        socialMedia {
          engagementRate
          error
          followingCnt
          id
          profileName
          sourceKey
          type
          url
          userId
          username
        }
        storeDetails {
          storeId
          storeName
          avatarURL
          socialFollowing
          country
          gender
          birthDate
          athleticLevel
        }
      }
      searchCriteria {
        id
        athleteMaxAge
        athleteMinAge
        athleticLevels
        budget
        flatFeePerStore
        genders
        maxFollowers
        minFollowers
        smPlatforms
        sports
        roles
        locations {
          country
          state
          city
          latitude
          longitude
          placeId
          placeText
          radius
        }
      }
      limit
      offset
      total
    }
  }
`;

export const GET_CB_APPLICATION_ANSWERS = gql`
  query GetCbApplicationAnswers($input: GetCbApplicationAnswersInput!) {
    getCbApplicationAnswers(input: $input) {
      id
      answer
      originalAnswer
      question {
        id
        question
        type
        order
        isRequired
      }
    }
  }
`;

export const GET_CAMPAIGN_LOCK = gql`
  query GetCampaignLock($input: GetCampaignLockInput!) {
    getCampaignLock(input: $input) {
      id
      createdAt
      expiresAt
      userEmail
      userFirstName
      userLastName
      userId
      userRole
    }
  }
`;

export const GET_DELIVERABLE_REVISION_LIMIT = gql`
  query GetDeliverableRevisionLimit {
    getDeliverableRevisionLimit
  }
`;

export const GET_CB_DELIVERABLE_ITEM_VOTES = gql`
  query GetCbDeliverableVotesV2($input: GetCbDeliverableVotesInput!) {
    getCbDeliverableVotesV2(input: $input) {
      user {
        id
        firstName
        lastName
        email
      }
      vote
      item {
        id
      }
    }
  }
`;

export const GET_TASK_EXTENSION_PENDING = gql`
  query GetCbPendingExtensionRequests(
    $input: GetCbPendingExtensionRequestsInput!
  ) {
    getCbPendingExtensionRequests(input: $input) {
      id
      deliverableId
      reason
      requestedExtensionDays
      status
      type
      createdAt
      updatedAt
      requestedBy {
        id
        firstName
        lastName
        email
      }
      deliverable {
        id
        status
        contentDueDateUtc
        contentDueDateFrom
        contentDueDateTo
        publishDueDateUtc
        publishDueDateFrom
        publishDueDateTo
        task {
          id
          orderIdx
        }
      }
    }
  }
`;

export const GET_CB_TASK_EXTENSION_HISTORY = gql`
  query GetCbTaskExtensionHistory($input: GetCbTaskExtensionHistoryInput!) {
    getAgencyCbTaskExtensionHistory(input: $input) {
      id
      reason
      requestedExtensionDays
      status
      type
      createdAt
      updatedAt
      deliverable {
        id
        status
        createdAt
        updatedAt
      }
      requestedBy {
        id
        firstName
        lastName
        email
      }
      reviewedBy {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const GET_AUDIENCE_FILTERS = gql`
  query GetAudienceFilters {
    getAudienceFilters {
      ageRanges {
        label
        value
      }
      ethnicities {
        label
        value
      }
      genders {
        label
        value
      }
      interests {
        label
        value
      }
    }
  }
`;

export const GET_VIDEO_TRANSCODING_JOBS = gql`
  query GetVideoTranscodingJobs($input: GetVideoTranscodingJobsInput!) {
    getVideoTranscodingJobs(input: $input) {
      id
      status
      progress
      inputKey
      resultKey
      errorMessage
      createdAt
      duration
      subtype
      thumbnailKey
    }
  }
`;

export const GET_SOCIAL_BOOST_CAMPAIGN_OFFERS = gql`
  query GetSocialBoostCampaignOffers(
    $input: GetSocialBoostCampaignOffersInput!
  ) {
    getSocialBoostCampaignOffers(input: $input) {
      total
      limit
      offset
      entities {
        id
        firstName
        lastName
        avatarUrl
        statusV2
        submittedAt
        updatedAt
        deliverablesV2 {
          id
          status
          items {
            id
            type
            ... on CbDeliverableLinkItemV2 {
              link
            }
            ... on CbDeliverableFilesItemV2 {
              files {
                key
                name
                url
              }
            }
            ... on CbDeliverableCopyV2 {
              copy
            }
          }
          revisions {
            id
            createdAt
            performedByEmail
            items {
              id
              feedback
              taskTitle
            }
          }
        }
      }
    }
  }
`;

export const GET_SOCIAL_BOOST_CAMPAIGN_SHARES = gql`
  query GetSocialBoostCampaignShares(
    $input: GetSocialBoostCampaignSharesInput!
  ) {
    getSocialBoostCampaignShares(input: $input) {
      totalShares
      verified
      pendingReview
      disputeInReview
    }
  }
`;

/** Lightweight offers list for custom-campaign preselected talent editing. */
export const GET_CUSTOM_CAMPAIGN_DRAFT_OFFERS = gql`
  query GetCustomCampaignDraftOffers($input: GetCampaignOffersInput!) {
    getCampaignOffers(input: $input) {
      total
      entities {
        id
        userId
        firstName
        lastName
        profileName
        avatarUrl
        slug
        socialFollowing
        user {
          avatarUrl
          socialMedia {
            type
            followingCnt
          }
        }
      }
    }
  }
`;

export const GET_CAMPAIGN_AGREEMENT_SIGNATORY_CANDIDATES = gql`
  query GetCampaignAgreementSignatoryCandidates(
    $input: GetCampaignAgreementSignatoryCandidatesInput!
  ) {
    getCampaignAgreementSignatoryCandidates(input: $input) {
      id
      firstName
      lastName
      email
    }
  }
`;
